import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CreditCard, Download, Loader2 } from 'lucide-react';
import { repairerInvoiceApi, RepairerInvoiceListItem } from '../../api/repairerInvoiceApi';
import { formatPrice } from '@/utils/priceHelpers';
import MarkPaidDialog from './MarkPaidDialog';
import { useAuthStore } from '@/routes/outil-pro/stores/auth-store';
import { useEffectiveConfiguration } from '@/routes/outil-pro/features/societies/hooks/useSocietyConfiguration';

/**
 * Page "Factures réparateur" côté outil-pro vendeur.
 *
 * 3 onglets :
 *   - À payer (SENT_TO_GARANT)
 *   - Payées en attente confirmation (PAID_BY_GARANT)
 *   - Historique (PAYMENT_CONFIRMED + CANCELLED)
 *
 * Cf SPECS/repairer_payment/README.md Phase 4.2.
 */
type Tab = 'to_pay' | 'pending_confirmation' | 'history';

const TAB_STATUSES: Record<Tab, string[]> = {
  to_pay: ['SENT_TO_GARANT'],
  // PAYMENT_DISPUTED reste visible ici : du point de vue du vendeur, il a
  // payé et la confirmation est en attente — la vérification (litige
  // réparateur) est pilotée par le gestionnaire Affinicar, sans action
  // vendeur dans l'outil. Label neutre « Vérification en cours ».
  pending_confirmation: ['PAID_BY_GARANT', 'PAYMENT_DISPUTED'],
  history: ['PAYMENT_CONFIRMED', 'CANCELLED'],
};

export default function RepairerInvoicesPage() {
  const { hasPermission, currentUser } = useAuthStore();
  const { data: effectiveConfig, isLoading: cfgLoading } =
    useEffectiveConfiguration(currentUser?.societyId || undefined);

  // Éligibilité identique au menu sidebar : droit signer/gérer les SAV ET
  // cycle paiement activé chez le garant. Ferme le deep-link : un garant sans
  // cycle qui taperait /sav/repairer-invoices en dur ne voit rien et ne
  // déclenche aucun appel liste.
  const eligible =
    (hasPermission('sav.sign') || hasPermission('sav.manage')) &&
    !!effectiveConfig?.paymentCycleEnabled;

  const [tab, setTab] = useState<Tab>('to_pay');
  const [invoices, setInvoices] = useState<RepairerInvoiceListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState<RepairerInvoiceListItem | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await repairerInvoiceApi.list(TAB_STATUSES[tab]);
      setInvoices(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eligible) refresh();
  }, [tab, eligible]);

  // Tant que la config n'est pas résolue, on n'affiche rien (évite un flash de
  // page ou une redirection prématurée). Non éligible → redirection accueil.
  if (cfgLoading) return null;
  if (!eligible) return <Navigate to="/" replace />;

  return (
    <div className="tw-p-6">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <CreditCard size={24} className="tw-text-affinicar-primary" />
        <h1 className="tw-text-xl tw-font-bold">Factures réparateur</h1>
      </div>

      <div className="tw-flex tw-gap-2 tw-border-b tw-mb-4">
        <TabBtn label="À payer" active={tab === 'to_pay'} onClick={() => setTab('to_pay')} />
        <TabBtn
          label="Payées en attente confirmation"
          active={tab === 'pending_confirmation'}
          onClick={() => setTab('pending_confirmation')}
        />
        <TabBtn label="Historique" active={tab === 'history'} onClick={() => setTab('history')} />
      </div>

      {loading ? (
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-gray-500 tw-py-8 tw-justify-center">
          <Loader2 className="tw-animate-spin" size={18} /> Chargement…
        </div>
      ) : invoices.length === 0 ? (
        <p className="tw-text-gray-500 tw-py-8 tw-text-center">Aucune facture dans cette catégorie.</p>
      ) : (
        <table className="tw-w-full tw-bg-white tw-rounded tw-shadow tw-text-sm">
          <thead className="tw-bg-gray-50 tw-text-left">
            <tr>
              <th className="tw-p-3">SAV</th>
              <th className="tw-p-3">Facture</th>
              <th className="tw-p-3">Montant</th>
              <th className="tw-p-3">Reçue le</th>
              <th className="tw-p-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="tw-border-t hover:tw-bg-gray-50">
                <td className="tw-p-3">
                  <Link to={`/sav/${inv.savId}#paiement`} className="tw-text-affinicar-primary tw-underline">
                    #{inv.savId}
                  </Link>
                </td>
                <td className="tw-p-3">
                  <span className="tw-inline-flex tw-items-center tw-gap-2">
                    {inv.invoiceNumber}
                    {inv.hasDocument && (
                      <button
                        type="button"
                        title="Télécharger la facture"
                        onClick={() => repairerInvoiceApi.downloadDocument(inv.id, `facture_${inv.invoiceNumber || inv.savId}`)}
                        className="tw-text-affinicar-primary hover:tw-opacity-70"
                      >
                        <Download size={16} />
                      </button>
                    )}
                  </span>
                </td>
                <td className="tw-p-3 tw-font-medium">{formatPrice(inv.amountTtc)}</td>
                <td className="tw-p-3 tw-text-gray-500">
                  {inv.sentToGarantAt && new Date(inv.sentToGarantAt).toLocaleDateString('fr-FR')}
                </td>
                <td className="tw-p-3 tw-text-right">
                  {tab === 'to_pay' && (
                    <button
                      type="button"
                      onClick={() => setMarkPaidTarget(inv)}
                      className="tw-bg-affinicar-primary tw-text-white tw-px-3 tw-py-1.5 tw-rounded tw-font-medium tw-text-sm hover:tw-opacity-90"
                    >
                      J'ai payé
                    </button>
                  )}
                  {tab === 'pending_confirmation' && inv.status === 'PAYMENT_DISPUTED' && (
                    <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-bg-gray-100 tw-text-gray-600">
                      Vérification en cours
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {markPaidTarget && (
        <MarkPaidDialog
          invoice={markPaidTarget}
          onClose={() => setMarkPaidTarget(null)}
          onPaid={async () => {
            setMarkPaidTarget(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-border-b-2 ${
        active
          ? 'tw-border-affinicar-primary tw-text-affinicar-primary'
          : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}
